import React from 'react';

// v2.0 AVATARS: shared avatar rendering + option lists.

export const AVATAR_EMOJIS = [
  '💀', '🤡', '🧠', '👁', '🩸', '🌀', '🔥', '⚡', '🥶', '😭',
  '💅', '🩻', '🦴', '🕳', '🫠', '👾', '👹', '👺', '🧟', '🧌',
  '🥸', '😈', '👽', '🤖', '🦾', '🕶', '🐸', '🐈‍⬛', '🦇', '🐍',
  '🩷', '💎', '🍜', '🧿', '📿', '🎭', '🃏', '💰', '🎯', '🌈',
];

export const AVATAR_COLORS = [
  { key: 'neon',   name: 'NEON GREEN', hex: '#39ff14' },
  { key: 'pink',   name: 'HOT PINK',   hex: '#ff007f' },
  { key: 'cyan',   name: 'CYBER CYAN', hex: '#00e5ff' },
  { key: 'gold',   name: 'GOLD',       hex: '#ffd700' },
  { key: 'red',    name: 'BLOOD RED',  hex: '#ff2b2b' },
  { key: 'violet', name: 'VOID VIOLET',hex: '#b46bff' },
  { key: 'lime',   name: 'ACID LIME',  hex: '#a0ff2e' },
  { key: 'orange', name: 'FLAME',      hex: '#ff8c1a' },
  { key: 'white',  name: 'GHOST WHITE',hex: '#f5f5f5' },
];

export const DEFAULT_AVATAR = { emoji: '💀', color: 'neon', title: null };

export function colorHexFor(colorKey) {
  return AVATAR_COLORS.find(c => c.key === colorKey)?.hex || '#39ff14';
}

// Compact avatar chip used everywhere (menu, combat portrait, leaderboard rows).
// v3.2: optional frame prop applies a cosmetic border via CSS class.
export function AvatarChip({ avatar, size = 48, testid, title, frame }) {
  const a = { ...DEFAULT_AVATAR, ...(avatar || {}) };
  const hex = colorHexFor(a.color);
  const hasDrawing = typeof a.drawing === 'string' && a.drawing.startsWith('data:image');
  // v3.2: frame css class (imported here via optional chaining to avoid import cycle)
  const frameCls = frame ? frameToClass(frame) : '';
  const solidColor = frame ? frameSolidColor(frame) : null;
  return (
    <div
      className={`avatar-chip ${frameCls}`}
      data-testid={testid}
      title={title}
      style={{
        width: size,
        height: size,
        fontSize: Math.floor(size * 0.6),
        borderColor: hex,
        boxShadow: frameCls ? 'none' : `${Math.max(2, size * 0.05)}px ${Math.max(2, size * 0.05)}px 0 ${hex}, 0 0 ${size * 0.4}px ${hex}66`,
        color: hex,
        ...(solidColor ? { '--frame-solid-color': solidColor } : {}),
      }}
    >
      {hasDrawing ? (
        <img
          src={a.drawing}
          alt=""
          data-testid={testid ? `${testid}-drawing` : undefined}
          className="avatar-chip-drawing"
          style={{ width: '100%', height: '100%', objectFit: 'contain', imageRendering: 'pixelated' }}
          draggable={false}
        />
      ) : (
        <span className="avatar-chip-emoji">{a.emoji}</span>
      )}
    </div>
  );
}

// Local frame → css class map (kept inline to avoid a circular import cycle if frames.js grows).
// v3.19: extended to cover 40 purchasable + 4 milestone frames. Frames without bespoke CSS
// fall back to `.frame-solid` which uses the frame's color as a solid outline (see index.css).
const FRAME_BESPOKE = {
  milestone_bronze:    'frame-bronze',
  milestone_silver:    'frame-silver',
  milestone_gold:      'frame-gold',
  milestone_prismatic: 'frame-prismatic',
  buy_cursed_pink:     'frame-cursed-pink',
  buy_holo_rainbow:    'frame-holo-rainbow',
  buy_animated_flames: 'frame-animated-flames',
  // v3.19 whale/fancy tier bespoke animations
  buy_godtier_mythic:  'frame-godtier-mythic',
  buy_absolute_sigma:  'frame-absolute-sigma',
  buy_divine_overlord: 'frame-divine-overlord',
  buy_absolute_slop:   'frame-absolute-slop',
  buy_cursed_century:  'frame-cursed-century',
  buy_prism_shatter:   'frame-prism-shatter',
  buy_neon_vortex:     'frame-neon-vortex',
  buy_cosmic_void:     'frame-cosmic-void',
  buy_aurora:          'frame-aurora',
  buy_solar_flare:     'frame-solar-flare',
};
// Frame color map — used by the .frame-solid fallback so any new frame still renders distinctly.
// Kept in sync with /app/backend/routes/cosmetics_data.py FRAMES[].color.
const FRAME_COLOR = {
  buy_neon_green_line:  '#39ff14',
  buy_neon_pink_line:   '#ff2ea6',
  buy_neon_cyan_line:   '#00e5ff',
  buy_neon_yellow_line: '#ffd700',
  buy_neon_red_line:    '#ff2b2b',
  buy_dashed_frame:     '#c0c8d8',
  buy_dotted_frame:     '#c0c8d8',
  buy_double_line:      '#c0c8d8',
  buy_corner_brackets:  '#39ff14',
  buy_thick_black:      '#111111',
  buy_ribbon_red:       '#ff4d4d',
  buy_ribbon_blue:      '#5aa8ff',
  buy_ribbon_purple:    '#c084fc',
  buy_pixel_frame:      '#39ff14',
  buy_glitch_lite:      '#ff2ea6',
  buy_cyber_grid:       '#00e5ff',
  buy_vaporwave:        '#ff77e0',
  buy_blood_runes:      '#ff2b2b',
  buy_ice_crystal:      '#a8dcff',
  buy_emerald_vine:     '#39ff14',
  buy_molten_copper:    '#e07d3a',
  buy_static_noise:     '#c0c8d8',
  buy_glitch_bar:       '#ff2ea6',
  buy_blueprint_grid:   '#5aa8ff',
  buy_radioactive:      '#c8ff2b',
  buy_bioluminescent:   '#5affe0',
  buy_diamond_refract:  '#e0e0ff',
};
function frameToClass(id) {
  return FRAME_BESPOKE[id] || (FRAME_COLOR[id] ? 'frame-solid' : '');
}
function frameSolidColor(id) {
  return FRAME_BESPOKE[id] ? null : (FRAME_COLOR[id] || null);
}
export { frameSolidColor };
